import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Tag,
  Button,
  Space,
  Table,
  Avatar,
  Typography,
  Tooltip,
  Badge,
  Select,
} from 'antd';
import {
  HeartOutlined,
  MessageOutlined,
  StarOutlined,
  EyeOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Text, Title } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

// Mock数据接口
interface RankNoteItem {
  id: string;
  rank: number;
  title: string;
  coverUrl: string;
  category: string;
  tags: string[];
  author: {
    nickname: string;
    avatar: string;
    isVerified: boolean;
  };
  publishTime: string;
  likedCount: number;
  commentsCount: number;
  collectedCount: number;
  interactiveCount: number;
  estimatedReads: number;
}

// Mock数据
const mockData: RankNoteItem[] = [
  {
    id: '1',
    rank: 1,
    title: '存一些海边有趣的沙滩拍照姿势😍',
    coverUrl: 'https://picsum.photos/120/120?random=1',
    category: '旅游出行',
    tags: ['旅游出行', '摄影', '旅行', '沙滩', '...'],
    author: {
      nickname: '大白免',
      avatar: 'https://picsum.photos/32/32?random=1',
      isVerified: true,
    },
    publishTime: '2025-08-19 11:00',
    likedCount: 9227,
    commentsCount: 64,
    collectedCount: 5924,
    interactiveCount: 15200,
    estimatedReads: 34660,
  },
  {
    id: '2',
    rank: 2,
    title: '这种恶心东西居然能卖出千份',
    coverUrl: 'https://picsum.photos/120/120?random=2',
    category: '旅游出行',
    tags: ['旅游出行', '女性安全', '旅游出行', '出行提醒'],
    author: {
      nickname: '毛毛兔兔',
      avatar: 'https://picsum.photos/32/32?random=2',
      isVerified: true,
    },
    publishTime: '2025-08-22 09:56',
    likedCount: 12700,
    commentsCount: 708,
    collectedCount: 282,
    interactiveCount: 13600,
    estimatedReads: 31540,
  },
  {
    id: '3',
    rank: 3,
    title: '镰仓江之岛做顶链的老爷爷真的要让我哭死了😭',
    coverUrl: 'https://picsum.photos/120/120?random=3',
    category: '旅游出行',
    tags: ['旅游出行', '镰仓', '日本', '江之岛', '...'],
    author: {
      nickname: 'Henna C',
      avatar: 'https://picsum.photos/32/32?random=3',
      isVerified: true,
    },
    publishTime: '2025-08-18 14:46',
    likedCount: 7864,
    commentsCount: 1132,
    collectedCount: 4007,
    interactiveCount: 13000,
    estimatedReads: 28130,
  },
  {
    id: '4',
    rank: 4,
    title: '乌兰哈达火山保活见效阳澄游客乱扔烟雾棒被打',
    coverUrl: 'https://picsum.photos/120/120?random=4',
    category: '旅游出行',
    tags: ['旅游出行', '热点', '火山', '草原旅游', '...'],
    author: {
      nickname: '来口雪碧喝啊',
      avatar: 'https://picsum.photos/32/32?random=4',
      isVerified: true,
    },
    publishTime: '2025-08-20 09:02',
    likedCount: 10600,
    commentsCount: 1533,
    collectedCount: 187,
    interactiveCount: 12300,
    estimatedReads: 27170,
  },
  {
    id: '5',
    rank: 5,
    title: '都别睡了！！！我醒！！',
    coverUrl: 'https://picsum.photos/120/120?random=5',
    category: '旅游出行',
    tags: ['旅游出行', '生活', '日常', '搞笑'],
    author: {
      nickname: '小红薯用户',
      avatar: 'https://picsum.photos/32/32?random=5',
      isVerified: false,
    },
    publishTime: '2025-08-21 07:30',
    likedCount: 8543,
    commentsCount: 432,
    collectedCount: 156,
    interactiveCount: 9131,
    estimatedReads: 25460,
  },
];

const categories = [
  { label: '笔记分类', value: '' },
  { label: '美妆', value: '美妆' },
  { label: '美食个护', value: '美食个护' },
  { label: '鞋包潮流', value: '鞋包潮流' },
  { label: '穿搭打扮', value: '穿搭打扮' },
  { label: '美食', value: '美食' },
  { label: '母婴育儿', value: '母婴育儿' },
  { label: '旅游出行', value: '旅游出行' },
  { label: '家居家装', value: '家居家装' },
  { label: '教育', value: '教育' },
  { label: '生活', value: '生活' },
  { label: '运动健身', value: '运动健身' },
  { label: '兴趣爱好', value: '兴趣爱好' },
  { label: '影视综', value: '影视综' },
  { label: '婚嫁', value: '婚嫁' },
  { label: '摄影摄像', value: '摄影摄像' },
  { label: '萌宠', value: '萌宠' },
  { label: '情感星座', value: '情感星座' },
];

const NoteRank: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('lowFansRank');
  const [selectedCategory, setSelectedCategory] = useState<string>('旅游出行');
  const [noteType, setNoteType] = useState<string>('');
  const [timeRange, setTimeRange] = useState<string>('近24小时');
  const [sortType, setSortType] = useState<string>('互动总数最高');
  const [dataSource, setDataSource] = useState<RankNoteItem[]>(mockData);
  const [loading, setLoading] = useState<boolean>(false);

  // 格式化数字显示
  const formatNumber = (num: number): string => {
    if (num >= 10000) {
      return (num / 10000).toFixed(2) + 'w';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  };

  // 获取排名徽章颜色
  const getRankBadgeColor = (rank: number): string => {
    switch (rank) {
      case 1:
        return '#FFD700'; // 金色
      case 2:
        return '#C0C0C0'; // 银色
      case 3:
        return '#CD7F32'; // 铜色
      default:
        return '#1890ff'; // 蓝色
    }
  };

  // 表格列定义
  const columns: ColumnsType<RankNoteItem> = [
    {
      title: '排名',
      key: 'rank',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {record.rank <= 3 ? (
            <Badge
              count={record.rank}
              style={{
                backgroundColor: getRankBadgeColor(record.rank),
                color: 'white',
                fontWeight: 'bold',
                fontSize: '14px',
                width: '24px',
                height: '24px',
                lineHeight: '24px',
                borderRadius: '50%',
              }}
            />
          ) : (
            <Text
              style={{ fontSize: '16px', fontWeight: 'bold', color: '#666' }}
            >
              {record.rank}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: '笔记信息',
      key: 'noteInfo',
      width: 400,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 12 }}>
          <img
            src={record.coverUrl}
            alt={record.title}
            style={{
              width: 80,
              height: 80,
              borderRadius: 8,
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: 8,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.4,
              }}
            >
              {record.title}
            </div>
            <div style={{ marginBottom: 8 }}>
              <Space size={4} wrap>
                <Tag color="blue" style={{ fontSize: '12px' }}>
                  {record.category}
                </Tag>
                {record.tags.slice(0, 4).map((tag, index) => (
                  <Tag key={index} style={{ fontSize: '12px' }}>
                    {tag}
                  </Tag>
                ))}
              </Space>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar size={20} src={record.author.avatar} />
              <Text style={{ fontSize: '12px', color: '#666' }}>
                {record.author.nickname}
              </Text>
              {record.author.isVerified && (
                <Badge
                  count="达人"
                  style={{ backgroundColor: '#1890ff', fontSize: '10px' }}
                />
              )}
              <Text style={{ fontSize: '12px', color: '#999' }}>
                发布于{record.publishTime}
              </Text>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: '点赞数',
      key: 'likedCount',
      width: 100,
      align: 'center',
      sorter: (a, b) => a.likedCount - b.likedCount,
      render: (_, record) => (
        <Text style={{ fontSize: '14px', fontWeight: 500 }}>
          {formatNumber(record.likedCount)}
        </Text>
      ),
    },
    {
      title: '评论数',
      key: 'commentsCount',
      width: 100,
      align: 'center',
      sorter: (a, b) => a.commentsCount - b.commentsCount,
      render: (_, record) => (
        <Text style={{ fontSize: '14px', fontWeight: 500 }}>
          {record.commentsCount}
        </Text>
      ),
    },
    {
      title: '收藏数',
      key: 'collectedCount',
      width: 100,
      align: 'center',
      sorter: (a, b) => a.collectedCount - b.collectedCount,
      render: (_, record) => (
        <Text style={{ fontSize: '14px', fontWeight: 500 }}>
          {formatNumber(record.collectedCount)}
        </Text>
      ),
    },
    {
      title: (
        <Tooltip title="点赞数+评论数+收藏数">
          <span>
            互动总数 <EyeOutlined style={{ fontSize: '12px', color: '#999' }} />
          </span>
        </Tooltip>
      ),
      key: 'interactiveCount',
      width: 120,
      align: 'center',
      sorter: (a, b) => a.interactiveCount - b.interactiveCount,
      render: (_, record) => (
        <Text style={{ fontSize: '14px', fontWeight: 600 }}>
          {formatNumber(record.interactiveCount)}
        </Text>
      ),
    },
    {
      title: '预估阅读数',
      key: 'estimatedReads',
      width: 120,
      align: 'center',
      sorter: (a, b) => a.estimatedReads - b.estimatedReads,
      render: (_, record) => (
        <Text style={{ fontSize: '14px', color: '#666' }}>
          ≈{formatNumber(record.estimatedReads)}
        </Text>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Button type="link" size="small">
            收藏
          </Button>
          <Button type="link" size="small">
            详情
          </Button>
        </Space>
      ),
    },
  ];

  // 筛选条件改变处理
  const handleFilterChange = () => {
    setLoading(true);
    // 这里应该调用实际的API接口
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  useEffect(() => {
    handleFilterChange();
  }, [selectedCategory, noteType, timeRange, sortType]);

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <Card>
        {/* 标签页 */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          style={{ marginBottom: '16px' }}
        >
          <TabPane
            tab={<span style={{ padding: '8px 16px' }}>爆增笔记排行</span>}
            key="increaseRank"
          />
          <TabPane
            tab={<span style={{ padding: '8px 16px' }}>商业笔记排行</span>}
            key="businessRank"
          />
          <TabPane
            tab={
              <span
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                }}
              >
                低粉爆款排行
              </span>
            }
            key="lowFansRank"
          />
        </Tabs>

        {/* 筛选条件 */}
        <div
          style={{
            background: '#fafafa',
            padding: '16px',
            borderRadius: '6px',
            marginBottom: '24px',
          }}
        >
          <Space size="large" wrap>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text strong>笔记分类</Text>
              <Select
                value={selectedCategory}
                onChange={setSelectedCategory}
                style={{ width: 120 }}
                size="small"
              >
                {categories.map((cat) => (
                  <Option key={cat.value} value={cat.value}>
                    {cat.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text strong>笔记形式</Text>
              <Space>
                <Button
                  size="small"
                  type={noteType === '' ? 'primary' : 'default'}
                  style={{ borderRadius: '16px' }}
                  onClick={() => setNoteType('')}
                >
                  图文笔记
                </Button>
                <Button
                  size="small"
                  type={noteType === 'video' ? 'primary' : 'default'}
                  style={{ borderRadius: '16px' }}
                  onClick={() => setNoteType('video')}
                >
                  视频笔记
                </Button>
              </Space>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text strong>统计时间</Text>
              <Space>
                <Button
                  size="small"
                  type={timeRange === '近24小时' ? 'primary' : 'default'}
                  style={{ borderRadius: '16px' }}
                  onClick={() => setTimeRange('近24小时')}
                >
                  近24小时
                </Button>
                <Button
                  size="small"
                  type={timeRange === '近3天' ? 'primary' : 'default'}
                  style={{ borderRadius: '16px' }}
                  onClick={() => setTimeRange('近3天')}
                >
                  近3天
                </Button>
                <Button
                  size="small"
                  type={timeRange === '近7天' ? 'primary' : 'default'}
                  style={{ borderRadius: '16px' }}
                  onClick={() => setTimeRange('近7天')}
                >
                  近7天
                </Button>
              </Space>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text strong>排序</Text>
              <Space>
                <Button
                  size="small"
                  type={sortType === '按点赞数最高' ? 'primary' : 'default'}
                  style={{ borderRadius: '16px' }}
                  onClick={() => setSortType('按点赞数最高')}
                >
                  按点赞数最高
                </Button>
                <Button
                  size="small"
                  type={sortType === '按评论数最高' ? 'primary' : 'default'}
                  style={{ borderRadius: '16px' }}
                  onClick={() => setSortType('按评论数最高')}
                >
                  按评论数最高
                </Button>
                <Button
                  size="small"
                  type={sortType === '按收藏数最高' ? 'primary' : 'default'}
                  style={{ borderRadius: '16px' }}
                  onClick={() => setSortType('按收藏数最高')}
                >
                  按收藏数最高
                </Button>
                <Button
                  size="small"
                  type={sortType === '互动总数最高' ? 'primary' : 'default'}
                  style={{
                    borderRadius: '16px',
                    background:
                      sortType === '互动总数最高' ? '#ff4d4f' : undefined,
                    borderColor:
                      sortType === '互动总数最高' ? '#ff4d4f' : undefined,
                  }}
                  onClick={() => setSortType('互动总数最高')}
                >
                  互动总数最高！
                </Button>
              </Space>
            </div>
          </Space>

          <div
            style={{
              marginTop: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Text style={{ fontSize: '14px', color: '#999' }}>
                数据更新于 2025-08-24｜23时
              </Text>
              <Tooltip title="数据说明">
                <Button type="link" size="small" style={{ padding: 0 }}>
                  数据说明
                </Button>
              </Tooltip>
            </div>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              style={{ borderRadius: '4px' }}
            >
              导出结果
            </Button>
          </div>
        </div>

        {/* 数据表格 */}
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowKey="id"
          pagination={{
            current: 1,
            total: dataSource.length,
            pageSize: 20,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条/总共 ${total} 条`,
            showQuickJumper: true,
            showSizeChanger: true,
          }}
          scroll={{ x: 1200 }}
          style={{ background: 'white' }}
        />
      </Card>
    </div>
  );
};

export default NoteRank;
